import { useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

/**
 * The token from a mailed link, taken out of the address bar on arrival.
 *
 * The token has to arrive on the query, because that is what the mails already
 * sent link to and what the apps claim. It does not have to stay there: posting
 * it sets a password and answers with a session, so it is the whole credential,
 * and a url is the one part of a page that gets written down everywhere. So it
 * moves into history state, which the browser restores on a reload but never
 * writes into a url, an address bar, or browser history.
 *
 * Scrubbing it out of what Sentry sends covers errors, traces and breadcrumbs,
 * but session replay records navigations without passing through any of those
 * hooks. Taking it out of the url is what covers replay as well -- and the
 * shoulder of anybody looking at the screen.
 */
export const useLinkToken = (): string | undefined => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()

  const fromQuery = searchParams.get('token')
  const fromHistory = (location.state as { token?: string } | null)?.token

  useEffect(() => {
    // Nothing on the query means it has already been moved, or was never there
    // -- and returning here is also what stops this from clobbering state that
    // another page handed over, such as the address signup passes along.
    if (fromQuery == null) {
      return
    }

    // Replace rather than push, so the url carrying the token is not left
    // behind in the back stack either.
    navigate(location.pathname, { replace: true, state: { token: fromQuery } })
  }, [fromQuery, location.pathname, navigate])

  return fromQuery ?? fromHistory
}
